import {IDrcValidator} from "@buildwithflux/core";
import {
    AnyPcbBakedNode,
    DocumentUid,
    DRCReview,
    DRCReviewCategory,
    generateComponentLink,
    IElementsMap,
    IProblem,
    isDesignRuleCheckType,
    isElectricalRuleCheckType,
    PcbNodesMap,
    PcbNodeTypes,
    PcbRuleSetsMap,
    processSharedDRC,
    processSharedERC,
    Review,
    ReviewCategoryType,
} from "@buildwithflux/models";

// Re-export generateComponentLink for backwards compatibility
export {generateComponentLink};

/**
 * Type guard to check if a review is a validation review (DRC or ERC).
 * Returns true for both Design Rule Check and Electrical Rule Check types.
 * These reviews have a `checks` property with individual check items.
 */
export function isValidationReview(review: Review | DRCReview): review is DRCReview {
    return isDesignRuleCheckType(review.type) || isElectricalRuleCheckType(review.type);
}

export type Nodes = PcbNodesMap<AnyPcbBakedNode>;
export type Node = Nodes[keyof Nodes];

export function getLabelForPcbNode(node: {name?: string; uid: string; type?: string} | undefined) {
    if (!node) return "Unknown";
    // Traces don't have a significant name (unless the user changed it)
    if (node.name === "Trace" && node.type === PcbNodeTypes.routeSegment) {
        return `Trace ${node.uid.substring(0, 4)}`;
    }

    return node.name ?? node.uid;
}

/**
 * Checks if a validator is an ERC (Electrical Rule Check) validator.
 * ERC validators have problemTypeKey starting with "erc_" or are schematic-related checks
 * that need schematicElements data for conversion.
 *
 * Note: missing_footprints and manufacture_part_number are processed here because they need
 * schematicElements, but they're moved to their proper categories in useReviewCategories.
 */
function isERCValidator(validator: IDrcValidator): boolean {
    return (
        validator.problemTypeKey.startsWith("erc_") ||
        validator.problemTypeKey === "missing_footprints" ||
        validator.problemTypeKey === "manufacture_part_number"
    );
}

/**
 * Converts DRC and ERC validation results to review categories for display in the UI.
 * Uses shared processors from @buildwithflux/models to handle the conversion logic.
 */
export function convertDRCResultToReview(
    documentUid: DocumentUid,
    detectedProblems: {[key: string]: IProblem},
    checksInProgress: {[key: string]: boolean},
    validators: IDrcValidator[],
    nodes: Nodes,
    pcbLayoutRuleSets: PcbRuleSetsMap,
    schematicElements: IElementsMap,
    reviewsByUid: {[key: string]: Review},
    showHiddenChecks?: boolean,
): {drcCategory: DRCReviewCategory; ercCategory: DRCReviewCategory} {
    // Split validators into DRC (PCB layout) and ERC (schematic) validators
    const drcValidators: IDrcValidator[] = [];
    const ercValidators: IDrcValidator[] = [];

    for (const validator of validators) {
        if (isERCValidator(validator)) {
            ercValidators.push(validator);
        } else {
            drcValidators.push(validator);
        }
    }

    // Use shared DRC processor for Design Rule Checks
    const drcCategory = processSharedDRC({
        documentUid,
        detectedProblems,
        checksInProgress,
        validators: drcValidators,
        nodes,
        pcbLayoutRuleSets,
        reviewsByUid,
        showHiddenChecks,
    });

    // Use shared ERC processor for Electrical Rule Checks
    const ercResult = processSharedERC({
        documentUid,
        detectedProblems,
        checksInProgress,
        validators: ercValidators,
        schematicElements,
        reviewsByUid,
        showHiddenChecks,
    });

    // Create ERC category with correct name (without mutating the original)
    const ercCategory: DRCReviewCategory = {
        ...ercResult,
        name: ReviewCategoryType.ElectricalRuleChecks,
    };

    return {drcCategory, ercCategory};
}
